'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useCarouselCore } from './useCarouselCore';
import { CarouselCoreProps, CarouselCoreExtended, CarouselBreakpoint } from './types';
import {
  calculateItemWidth,
  calculatePaginationDots,
  calculateTransformOffset,
  getCurrentBreakpoint,
  shouldShowNavigation,
  checkDragThreshold,
  resetDragState,
  createTransformStyle,
  createContainerStyle,
  createItemStyle,
} from './utils';

import {
  MULTI_CARD_DEFAULT_BREAKPOINTS,
  MULTI_CARD_PEEK_BREAKPOINTS,
  MULTI_CARD_PEEK_ALL_BREAKPOINTS,
} from './config';

export const CAROUSEL_MULTI_CARD_VARIANTS = {
  DEFAULT: 'default',
  PEEK: 'peek',
  PEEK_ALL: 'peek-all',
  CUSTOM: 'custom',
} as const;

export type CarouselMultiCardVariant =
  (typeof CAROUSEL_MULTI_CARD_VARIANTS)[keyof typeof CAROUSEL_MULTI_CARD_VARIANTS];

export interface UseCarouselMultiCardProps extends CarouselCoreProps {
  /** Carousel variant that determines breakpoint behavior */
  variant?: CarouselMultiCardVariant;
  /** Custom breakpoint configuration (only used when variant is 'custom') */
  customBreakpoints?: CarouselBreakpoint[];
  /** Gap between items in pixels */
  gap?: number;
  /** Threshold for snapping to next/previous item (0-1) */
  snapThreshold?: number;
  /** Right-to-left layout; inverts slide direction and drag interpretation */
  rtl?: boolean;
}

export interface UseCarouselMultiCardReturn {
  /** Current active index */
  currentIndex: number;
  /** Number of visible cards based on viewport */
  visibleCards: number;
  /** Whether peek is shown at current viewport */
  showPeek: boolean;
  /** Peek amount percentage at current viewport */
  peekAmount: number;
  /** Whether we can go to previous item */
  canGoPrevious: boolean;
  /** Whether we can go to next item */
  canGoNext: boolean;
  /** Whether user is currently dragging */
  isDragging: boolean;
  /** Current drag offset for smooth transforms */
  dragOffset: number;
  /** Total number of pagination dots */
  totalDots: number;
  /** Go to previous item */
  goToPrevious: () => void;
  /** Go to next item */
  goToNext: () => void;
  /** Go to specific index */
  goToIndex: (index: number) => void;
  /** Ref for the carousel container */
  carouselRef: CarouselCoreExtended['carouselRef'];
  /** Handle keyboard navigation */
  handleKeyDown: (event: React.KeyboardEvent) => void;
  /** Handle mouse down for drag start */
  handleMouseDown: (event: React.MouseEvent) => void;
  /** Handle touch start for drag start */
  handleTouchStart: (event: React.TouchEvent) => void;
  /** Get transform style for smooth drag animation */
  getTransformStyle: () => React.CSSProperties;
  /** Get item width for layout calculations */
  getItemWidth: () => number;
  /** Get container styles for proper layout */
  getContainerStyles: () => React.CSSProperties;
  /** Get item styles for proper sizing */
  getItemStyles: () => React.CSSProperties;
  /** Check if pagination dot should be active */
  isDotActive: (dotIndex: number) => boolean;
}

/**
 * Unified Multi-Card Carousel Hook
 *
 * Supports different carousel variants:
 * - 'showcase': Multi-card showcase (1→2→3 cards, no peeking)
 * - 'preview': Smart peeking carousel (contextual peek behavior)
 * - 'custom': Custom breakpoint configuration
 *
 * Features:
 * - Responsive card layout based on variant
 * - Optional peeking behavior
 * - Dynamic pagination dots
 * - Left/right navigation buttons
 * - Drag enabled, no auto-advance
 * - Each click/swipe moves one card
 */
export const useCarouselMultiCard = ({
  variant = 'default',
  customBreakpoints,
  gap = 16,
  snapThreshold = 0.3,
  autoAdvance = false, // Multi-card carousels don't have auto-advance
  rtl = false,
  ...coreProps
}: UseCarouselMultiCardProps): UseCarouselMultiCardReturn => {
  const core = useCarouselCore({
    ...coreProps,
    autoAdvance,
  });

  // Get breakpoint configuration based on variant
  const breakpoints = useMemo(() => {
    switch (variant) {
      case 'default':
        return MULTI_CARD_DEFAULT_BREAKPOINTS;
      case 'peek':
        return MULTI_CARD_PEEK_BREAKPOINTS;
      case 'peek-all':
        return MULTI_CARD_PEEK_ALL_BREAKPOINTS;
      case 'custom':
        return customBreakpoints || MULTI_CARD_DEFAULT_BREAKPOINTS;
      default:
        return MULTI_CARD_DEFAULT_BREAKPOINTS;
    }
  }, [variant, customBreakpoints]);

  // Get responsive configuration
  const currentBreakpoint = getCurrentBreakpoint(core.extension.width, breakpoints);
  const visibleCards = currentBreakpoint.visibleCards;
  const hasMoreItemsThanVisible = coreProps.totalItems > visibleCards;
  const showPeek = (currentBreakpoint.showPeek || false) && hasMoreItemsThanVisible;
  const peekAmount = showPeek ? currentBreakpoint.peekAmount || 0 : 0;

  // Calculate navigation boundaries and pagination
  const canGoPrevious = shouldShowNavigation(
    core.currentIndex,
    coreProps.totalItems,
    visibleCards,
    coreProps.loop || false,
    'previous'
  );
  const canGoNext = shouldShowNavigation(
    core.currentIndex,
    coreProps.totalItems,
    visibleCards,
    coreProps.loop || false,
    'next'
  );
  core.extension.visibleCardsRef.current = visibleCards;
  const totalDots = calculatePaginationDots(coreProps.totalItems, visibleCards);

  // Get item width with variant-specific peek support
  const getItemWidth = useCallback(() => {
    if (!core.carouselRef.current) return 0;

    const element = core.carouselRef.current;
    const computedStyle = window.getComputedStyle(element);

    // Get the actual content width by subtracting padding from clientWidth
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const availableWidth = element.clientWidth - paddingLeft - paddingRight;

    return calculateItemWidth(
      availableWidth,
      visibleCards,
      gap,
      showPeek,
      peekAmount,
      coreProps.totalItems
    );
  }, [visibleCards, gap, showPeek, peekAmount, coreProps.totalItems, core.carouselRef]);

  // Set up custom drag end handler using the ref
  useEffect(() => {
    core.extension.dragEndHandlerRef.current = () => {
      // Use dragStateRef.current.currentX instead of core.dragOffset since that gets reset
      const currentDragOffset = core.extension.dragStateRef.current.currentX;
      const itemWidth = getItemWidth();

      // Check drag threshold using shared utility
      if (checkDragThreshold(currentDragOffset, itemWidth, snapThreshold)) {
        // LTR: drag left (negative) = next (+1); RTL: drag right (positive) = next (+1)
        const direction = rtl ? (currentDragOffset > 0 ? 1 : -1) : currentDragOffset < 0 ? 1 : -1;

        // Use the ref value instead of core.currentIndex which may be stale due to React's async state updates
        const actualCurrentIndex = core.extension.actualCurrentIndexRef.current;
        const targetIndex = actualCurrentIndex + direction;

        // Check boundaries
        if (targetIndex >= 0 && targetIndex <= coreProps.totalItems - visibleCards) {
          // Track swipe before updating index (if callback provided)
          if (core.extension.onSwipe) {
            const swipeDirection = direction > 0 ? 'left' : 'right'; // left = forward, right = backward
            const isTouch = !core.extension.dragStateRef.current.isMouseDrag;
            core.extension.onSwipe(targetIndex, swipeDirection, isTouch);
          }

          core.extension.updateIndex(targetIndex, visibleCards);
        }
      }

      // Reset drag state using shared utility
      resetDragState(
        core.extension.setDragOffset,
        core.extension.setIsDragging,
        autoAdvance,
        core.play
      );
    };

    // Return cleanup function
    return () => {
      core.extension.dragEndHandlerRef.current = null;
    };
  }, [
    getItemWidth,
    snapThreshold,
    coreProps.loop,
    coreProps.totalItems,
    visibleCards,
    autoAdvance,
    rtl,
    core,
  ]); // Include all dependencies

  // Memoize transform offset calculation for better performance with peek support
  const transformOffset = useMemo(() => {
    const itemWidth = getItemWidth();
    let ltrOffset: number;

    // For peek behavior, adjust transform when at the last position
    if (showPeek && !coreProps.loop) {
      const maxIndex = coreProps.totalItems - visibleCards;
      const isAtEnd = core.currentIndex >= maxIndex;

      if (isAtEnd && coreProps.totalItems > visibleCards) {
        // Simple calculation: total width of all items - visible area
        const containerWidth = core.carouselRef.current?.clientWidth || 0;
        const paddingLeft = core.carouselRef.current
          ? parseFloat(window.getComputedStyle(core.carouselRef.current).paddingLeft) || 0
          : 0;
        const paddingRight = core.carouselRef.current
          ? parseFloat(window.getComputedStyle(core.carouselRef.current).paddingRight) || 0
          : 0;
        const availableWidth = containerWidth - paddingLeft - paddingRight;

        // Total width of all items + gaps between them
        const totalItemsWidth = coreProps.totalItems * itemWidth + (coreProps.totalItems - 1) * gap;

        // Move left by (total width - visible width)
        const finalOffset = -(totalItemsWidth - availableWidth);

        ltrOffset = finalOffset + core.dragOffset;
      } else {
        ltrOffset = calculateTransformOffset(core.currentIndex, itemWidth, gap, core.dragOffset);
      }
    } else {
      ltrOffset = calculateTransformOffset(core.currentIndex, itemWidth, gap, core.dragOffset);
    }

    // RTL: invert translateX so higher index slides content right; -ltrOffset = base - dragOffset
    return rtl ? -ltrOffset : ltrOffset;
  }, [
    core.currentIndex,
    getItemWidth,
    gap,
    core.dragOffset,
    showPeek,
    visibleCards,
    coreProps.totalItems,
    coreProps.loop,
    rtl,
    core.carouselRef,
  ]);

  // Get transform style using memoized offset and centralized utility
  const getTransformStyle = useCallback((): React.CSSProperties => {
    return createTransformStyle({
      offset: transformOffset,
      isDragging: core.isDragging,
    });
  }, [transformOffset, core.isDragging]);

  // Get container styles using centralized utility
  const getContainerStyles = useCallback((): React.CSSProperties => {
    const transformStyle = getTransformStyle();
    return createContainerStyle({
      transformStyle,
      additionalStyles: {
        '--carousel-item-wrapper-gap': `${gap}px`,
      } as React.CSSProperties,
    });
  }, [getTransformStyle, gap]);

  // Get item styles for proper sizing using centralized utility
  const getItemStyles = useCallback((): React.CSSProperties => {
    const itemWidth = getItemWidth();

    return createItemStyle({
      itemWidth,
    });
  }, [getItemWidth]);

  // Check if pagination dot should be active
  const isDotActive = useCallback(
    (dotIndex: number): boolean => {
      if (coreProps.loop) {
        return dotIndex === core.currentIndex;
      }

      // For non-looping, each dot represents a position where that index would be the first visible
      return dotIndex === core.currentIndex;
    },
    [core.currentIndex, coreProps.loop]
  );

  // Enhanced navigation with visible cards context
  const goToPrevious = useCallback(() => {
    core.goToPrevious(visibleCards);
  }, [core, visibleCards]);

  const goToNext = useCallback(() => {
    core.goToNext(visibleCards);
  }, [core, visibleCards]);

  const goToIndex = useCallback(
    (index: number) => {
      core.goToIndex(index, visibleCards);
    },
    [core, visibleCards]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (rtl && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        const swappedEvent = {
          ...event,
          key: event.key === 'ArrowLeft' ? 'ArrowRight' : 'ArrowLeft',
        } as React.KeyboardEvent;
        core.handleKeyDown(swappedEvent, visibleCards);
      } else {
        core.handleKeyDown(event, visibleCards);
      }
    },
    [core, visibleCards, rtl]
  );

  return {
    currentIndex: core.currentIndex,
    visibleCards,
    showPeek,
    peekAmount,
    canGoPrevious,
    canGoNext,
    isDragging: core.isDragging,
    dragOffset: core.dragOffset,
    totalDots,
    goToPrevious,
    goToNext,
    goToIndex,
    carouselRef: core.carouselRef,
    handleKeyDown,
    handleMouseDown: core.handleMouseDown,
    handleTouchStart: core.handleTouchStart,
    getTransformStyle,
    getItemWidth,
    getContainerStyles,
    getItemStyles,
    isDotActive,
  };
};
